import React from 'react';
import { Box, Chip, Divider, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { EmailOutlined, ExpandMore } from '@mui/icons-material';
import { PublicUser, UserProfile } from '@uplift/api';
import { Alert } from '../../../../../Alert';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { PatientContactInformation } from '../ContactInformationAccordian/PatientContactInformation.component';
import { useBreakpoint } from '../../../../../hooks';
import { Button } from '../../../../../Button';
import { useMessagingContext } from '../../../../../Messaging/MessagingProvider.component';
import { usePatientMatchedProvider } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { Tooltip } from '../../../../../Tooltip';
import { UserContact } from '@uplift/types';


const useStyles = makeStyles<PatientProfileGeneralInfoProps>()(theme => ({
  root: {
    padding: theme.spacing(5, 3),
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(1),
    display: 'flex',
  },
  userName: {
    color: theme.palette.darkSlate.main,
    fontWeight: 700,
  },
  userNamePreferred: {
    color: theme.palette.darkSlate.main,
  },
}));


export type PatientProfileGeneralInfoProps = {
  user: PublicUser;
  profile?: UserProfile;
  contacts: UserContact[];
  compact?: boolean;
  expanded?: boolean;
  onExpandToggle?: () => void;
  expandPlacement?: 'top' | 'left';
};


const PatientProfileGeneralInfoContainer: React.FC<{ compact?: boolean }> = (props) => {
  if (props.compact) {
    return (
      <Box pl={2} pt={2} pb={2} pr={1} display="flex">
        {props.children}
      </Box>
    );
  }

  return (
    <Box pt={5} pb={3} px={3} display="flex">
      {props.children}
    </Box>
  );
};

const PatientProfileSubtitle: React.VFC<{ profile?: UserProfile }> = (props) => {
  if (props.profile?.pronouns && props.profile?.dob) {
    return (
      <Box display="flex" gap="8px">
        <Typography component="span" color="textSecondary">
          {CalendarDateService.format(props.profile?.dob, DateFormats['MM/DD/YYYY'], undefined)}
        </Typography>
        <Box>
          <Divider orientation="vertical" />
        </Box>
        <Typography component="span" color="textSecondary">
          {props.profile?.pronouns}
        </Typography>
      </Box>
    );
  }

  if (props.profile?.pronouns) {
    return (
      <Box>
        <Typography component="span" color="textSecondary">
          {props.profile?.pronouns}
        </Typography>
      </Box>
    );
  }

  if (props.profile?.dob) {
    return (
      <Box>
        <Typography component="span" color="textSecondary">
          {CalendarDateService.format(props.profile?.dob, DateFormats['MM/DD/YYYY'], undefined)}
        </Typography>
      </Box>
    );
  }

  return null;
};


export const PatientProfileGeneralInfo: React.FC<PatientProfileGeneralInfoProps> = ({ expanded, expandPlacement = 'left', ...props }) => {
  const provider = useCurrentuser();
  const { data: matchData } = usePatientMatchedProvider(props.user.id, provider.id);
  const { classes } = useStyles(props, { props });
  const breakpoints = useBreakpoint();
  const expandable = props.compact && breakpoints.sm;
  const expandedRotation = { left: 90, top: 180 }[expandPlacement];
  const titleVariant = props.compact ? 'h5' : 'h4';
  const messagingContext = useMessagingContext();
  const identityVerifiedAt = matchData?.match?.identityVerifiedAt;

  return (
    <PatientProfileGeneralInfoContainer compact={props.compact}>
      <Box flex="1" width="100%">
        <Box>
          <Typography className={classes.userName} component="span" variant={titleVariant}>
            {props.user.firstName} {props.user.lastName}
          </Typography>
          {props.profile?.preferredName && (
            <Typography component="span" variant={titleVariant}>
              ({props.profile.preferredName})
            </Typography>
          )}
          <PatientProfileSubtitle
            profile={props.profile}
          />
          {!!identityVerifiedAt && (
            <Box mt={0.5}>
              <Tooltip title={`Verified at ${CalendarDateService.date(identityVerifiedAt).format(DateFormats.DATETIME)}`}>
                <Chip size="small" label="Identity Verified" sx={{ userSelect: 'none' }} />
              </Tooltip>
            </Box>
          )}
        </Box>
        {props.user.status !== 'regular' && (
          <Box mt={2}>
            <Alert title="Signup Incomplete" severity="warning" icon={false}>
              Contact the client to encourage them create their UpLift account and complete their intake forms.
            </Alert>
          </Box>
        )}
        <Box mt={2}>
          <PatientContactInformation
            contacts={props.contacts}
            boldText
          />
        </Box>
        {props.profile && (
          <Button
            color="secondary"
            variant="contained"
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            startIcon={<EmailOutlined />}
            onClick={async () => {
              if (!props.profile) return;
              const conversationId = await messagingContext.createChat(props.profile.userId);

              messagingContext.openModal(conversationId);
            }}
          >Open Chat</Button>
        )}
      </Box>
      {expandable && (
        <Box display="flex" alignItems="center" justifyContent="center">
          <IconButton size="small" onClick={props.onExpandToggle}>
            <ExpandMore fontSize="large" style={{ transition: 'transform 140ms', transform: `rotate(${expanded ? expandedRotation : 0}deg)` }} />
          </IconButton>
        </Box>
      )}
    </PatientProfileGeneralInfoContainer>
  );
};
